import { alpha } from "@mui/material";

// Shared Staff Portal palette. Hoisted from local copies previously
// duplicated in Messages.tsx and MessageTemplates.tsx; ParentRequests.tsx
// is the third consumer that triggered the hoist (see memory:
// "Hoist shared primitive on second module").
//
// Values MUST stay in sync with the two original local copies so the
// visual language is identical across the Staff Portal.
export const C = {
  blue:   { fg: "#1d4ed8", bg: alpha("#3b82f6", 0.10), border: alpha("#3b82f6", 0.28) },
  teal:   { fg: "#0f766e", bg: alpha("#14b8a6", 0.10), border: alpha("#14b8a6", 0.28) },
  green:  { fg: "#15803d", bg: alpha("#22c55e", 0.10), border: alpha("#22c55e", 0.28) },
  indigo: { fg: "#4338ca", bg: alpha("#6366f1", 0.10), border: alpha("#6366f1", 0.28) },
  amber:  { fg: "#a16207", bg: alpha("#eab308", 0.10), border: alpha("#eab308", 0.28) },
  purple: { fg: "#6d28d9", bg: alpha("#7c3aed", 0.10), border: alpha("#7c3aed", 0.28) },
  rose:   { fg: "#b91c1c", bg: alpha("#ef4444", 0.10), border: alpha("#ef4444", 0.28) },
  navy:   { fg: "#0f172a", bg: "#f8fafc", border: "rgba(15,23,42,0.08)" },
} as const;

export type PaletteTone = keyof typeof C;

// Full-fill BRIGHT medium-saturation palette used by the Staff
// Portal DASHBOARD only (2026-09 Bright Balanced pass, superseding
// the earlier pastel and dark jewel-tone iterations). The
// translucent-alpha `C` above is intentionally left untouched so
// Messages / ParentRequests / other Staff Portal pages keep their
// existing near-white tinted chips.
//
// Every value is a SOLID hex. No gradients, no alpha overlays,
// no pale-pastel tints, no dark jewel tones. Field shape:
//   bg     — the whole card fill (medium-bright, colour is obvious
//            but the surface is still light enough for dark text)
//   border — a slightly deeper shade of the same hue
//   accent — a strong solid hue for icon tiles / buttons / big
//            metric emphasis
//   text   — primary heading colour on the card (dark navy on
//            every bright tone so the number / label reads crisp
//            against the mid-value background)
export const DASH_C = {
  blue:   { bg: "#BFD7FF", border: "#78A8F5", accent: "#2563EB", text: "#102A43" },
  sky:    { bg: "#BCEAFF", border: "#7AD3F7", accent: "#0284C7", text: "#123E56" },
  teal:   { bg: "#BFE9DF", border: "#7DD3C5", accent: "#0F766E", text: "#174C48" },
  cyan:   { bg: "#C2F3F6", border: "#7EDFE5", accent: "#0891B2", text: "#164E52" },
  emerald:{ bg: "#C8EECF", border: "#88D79A", accent: "#16A34A", text: "#1F5130" },
  indigo: { bg: "#D5D7FF", border: "#9EA2F5", accent: "#4F46E5", text: "#303070" },
  purple: { bg: "#DED0F8", border: "#B79BEA", accent: "#7C3AED", text: "#4E2F77" },
  amber:  { bg: "#F9D98C", border: "#EAB94D", accent: "#D97706", text: "#65420D" },
  orange: { bg: "#F9C99F", border: "#EE9F63", accent: "#EA580C", text: "#673512" },
  coral:  { bg: "#F6BDC5", border: "#EB8795", accent: "#E11D48", text: "#702533" },
  navy:   { bg: "#D9E8FF", border: "#9FC2FF", accent: "#2563EB", text: "#17324D" },
  slate:  { bg: "#E3E9F1", border: "#B7C3D4", accent: "#3B5064", text: "#25384A" },
} as const;

// Dashboard neutrals (spec §"PAGE BACKGROUND", §"TEXT").
//   pageBg    — light neutral so bright cards stand out; NOT dark
//   cardText  — primary heading colour on bright cards
//   metric    — big metric numbers on bright cards (deepest navy)
//   bodyText  — body copy on bright cards
//   secondary — secondary / caption text on bright cards
//   muted     — muted / hint text on bright cards
export const DASH_NEUTRAL = {
  pageBg:      "#F4F7FB",
  cardText:    "#17324D",
  metric:      "#102A43",
  bodyText:    "#334155",
  secondary:   "#64748B",
  muted:       "#7C8A99",
} as const;

export type DashTone = keyof typeof DASH_C;
